// Main Entry Point
// Orchestrates initialization of the entire application

import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/inter/latin-ext-400.css';
import '@fontsource/inter/latin-ext-500.css';
import '@fontsource/inter/latin-ext-600.css';
import '@fontsource/inter/latin-ext-700.css';
import './css/styles.css';

import { initAuthListener, isStaffUser } from './js/core/auth-manager.js';
import { initialViewRouter } from './js/core/router.js';
import { listenToQueue, listenToUsers } from './js/services/firestore-service.js';
import { initRegistrationForm, updateCustomerWaitingView } from './js/views/customer-view.js';
import { initStaffViewControls, updateStaffView, updateShiftAndPauseButtonsState, displayStaffEmail } from './js/views/staff-view.js';
import { initStaffViewV2Controls, updateStaffViewV2, loadStaffSellerInfo, displayStaffEmailV2 } from './js/views/staff-view-v2.js';
import { initHostViewControls, updateHostView, loadHostSellerInfo } from './js/views/host-view.js';
import { initSurveyForm } from './js/views/survey-view.js';
import { initSellerNameModal, initPreferentialModal, showSellerNameModal } from './js/components/modal.js';
import { initFilters } from './js/components/filters.js';
import { VIEWS } from './js/config/constants.js';
import stateManager from './js/core/state-manager.js';

// View elements cache
const views = {
    login: document.getElementById('login-view'),
    register: document.getElementById('register-view'),
    waiting: document.getElementById('waiting-view'),
    staff: document.getElementById('staff-view'),
    'staff-v2': document.getElementById('staff-view-v2'),
    host: document.getElementById('host-view'),
    survey: document.getElementById('survey-view'),
    tokenError: document.getElementById('token-error-view')
};

/**
 * Show a specific view (hide others)
 *
 * @param {string} viewName - Name of view to show
 */
function showView(viewName) {
    Object.values(views).forEach(view => view.classList.add('hidden'));

    if (views[viewName]) {
        views[viewName].classList.remove('hidden');
    }
}

/**
 * Start Firestore real-time listeners
 */
function startQueueListeners() {
    // Listen to queue changes
    listenToQueue((customers) => {
        stateManager.setAllCustomers(customers);

        // Update appropriate view
        if (views.staff.offsetParent) {
            updateStaffView(customers);
        }

        if (views['staff-v2'].offsetParent) {
            updateStaffViewV2(customers);
        }

        if (views.host.offsetParent) {
            updateHostView(customers);
        }

        // BUGFIX 2025-11-01: Use classList instead of offsetParent for mobile compatibility
        if (!views.waiting.classList.contains('hidden') && stateManager.getState('currentCustomerId')) {
            updateCustomerWaitingView(customers);
        }
    });

    // Listen to users/sellers changes
    listenToUsers((sellersData) => {
        stateManager.setSellersData(sellersData);

        // Update host view when sellers change
        if (views.host.offsetParent) {
            const allCustomers = stateManager.getState('allCustomers');
            updateHostView(allCustomers);
        }

        // Update staff-v2 view when sellers change
        if (views['staff-v2'].offsetParent) {
            const allCustomers = stateManager.getState('allCustomers');
            updateStaffViewV2(allCustomers);
        }

        // BUGFIX 2025-11-01: Use classList instead of offsetParent for mobile compatibility
        // This ensures sellersData updates trigger view refresh on mobile devices
        if (!views.waiting.classList.contains('hidden') && stateManager.getState('currentCustomerId')) {
            const allCustomers = stateManager.getState('allCustomers');
            updateCustomerWaitingView(allCustomers);
        }
    });
}

/**
 * Initialize login form
 */
function initLoginForm() {
    const form = document.getElementById('login-form');
    const loginError = document.getElementById('login-error');

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const email = form['login-email'].value;
        const password = form['login-password'].value;

        loginError.classList.add('hidden');

        try {
            const { signIn } = await import('./js/core/auth-manager.js');
            await signIn(email, password);
        } catch (error) {
            loginError.textContent = "Error: Email o contraseña incorrectos.";
            loginError.classList.remove('hidden');
        }
    });
}

/**
 * Initialize the application
 */
async function init() {
    console.log('🚀 Initializing Fila Virtual UMA...');

    // Initialize forms
    initLoginForm();
    initRegistrationForm(showView, startQueueListeners);
    initSurveyForm();

    // Initialize modals
    initSellerNameModal();
    initPreferentialModal();

    // Initialize staff controls
    initStaffViewControls();

    // Initialize staff v2 controls
    initStaffViewV2Controls();

    // Initialize host controls
    initHostViewControls();

    // Initialize filters
    initFilters((filter) => {
        const allCustomers = stateManager.getState('allCustomers');
        updateStaffView(allCustomers);
    });

    // Initialize auth listener
    // With anonymous auth, onAuthStateChanged ALWAYS fires (anonymous or staff)
    // so the router call inside the callback is sufficient
    initAuthListener((user, sellerInfo) => {
        if (user && isStaffUser(user)) {
            // Staff-specific initialization (not for anonymous customers)
            displayStaffEmail(user);
            displayStaffEmailV2(user);
            updateShiftAndPauseButtonsState();
            showSellerNameModal(sellerInfo);
            loadHostSellerInfo();
            loadStaffSellerInfo();
        }

        initialViewRouter(showView, startQueueListeners);
    });

    console.log('✅ Fila Virtual UMA initialized successfully');
}

// Start the application
init();
